import { frameBox, type TextMeasure } from './markupBounds.ts';
import { boxFromCorners, inflateBox, keepAspectCorner, snapAngle } from './markupConstraints.ts';
import { distance, type Size } from './markupGeometry.ts';
import { isSegmentShape, type Box, type MarkupShape, type Point } from './markupShapes.ts';

export type FrameHandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export type EndpointHandleId = 'from' | 'to';
export type HandleId = FrameHandleId | EndpointHandleId;

export interface Handle {
    id: HandleId;
    at: Point;
}

type Axis = -1 | 0 | 1;

const FRAME_HANDLES: Record<FrameHandleId, { x: Axis; y: Axis }> = {
    nw: { x: -1, y: -1 },
    n: { x: 0, y: -1 },
    ne: { x: 1, y: -1 },
    e: { x: 1, y: 0 },
    se: { x: 1, y: 1 },
    s: { x: 0, y: 1 },
    sw: { x: -1, y: 1 },
    w: { x: -1, y: 0 }
};

export const FRAME_HANDLE_IDS = Object.keys(FRAME_HANDLES) as readonly FrameHandleId[];

function isFrameHandle(id: HandleId): id is FrameHandleId {
    return id in FRAME_HANDLES;
}

function offsetPoint(point: Point, delta: Point): Point {
    return { x: point.x + delta.x, y: point.y + delta.y };
}

export function translateShape(shape: MarkupShape, delta: Point): MarkupShape {
    switch (shape.kind) {
        case 'pen':
            return { ...shape, points: shape.points.map((point) => offsetPoint(point, delta)) };
        case 'arrow':
        case 'line':
            return {
                ...shape,
                from: offsetPoint(shape.from, delta),
                to: offsetPoint(shape.to, delta)
            };
        case 'rect':
        case 'ellipse':
            return { ...shape, box: { ...shape.box, ...offsetPoint(shape.box, delta) } };
        case 'text':
            return { ...shape, at: offsetPoint(shape.at, delta) };
    }
}

function clampAxis(delta: number, start: number, length: number, limit: number): number {
    return Math.min(Math.max(delta, Math.min(0, -start)), Math.max(0, limit - start - length));
}

// Keeps a dragged mark on the photo; one already hanging off an edge may only come back.
export function clampMove(frame: Box, delta: Point, image: Size): Point {
    return {
        x: clampAxis(delta.x, frame.x, frame.width, image.width),
        y: clampAxis(delta.y, frame.y, frame.height, image.height)
    };
}

function handlePoint(frame: Box, axes: { x: Axis; y: Axis }): Point {
    return {
        x: frame.x + ((axes.x + 1) / 2) * frame.width,
        y: frame.y + ((axes.y + 1) / 2) * frame.height
    };
}

// `pad` pushes frame handles out from the frame; endpoint handles stay on the endpoints.
export function shapeHandles(shape: MarkupShape, measure: TextMeasure, pad = 0): readonly Handle[] {
    if (shape.kind === 'text') return [];
    if (isSegmentShape(shape)) {
        return [
            { id: 'from', at: shape.from },
            { id: 'to', at: shape.to }
        ];
    }
    const frame = inflateBox(frameBox(shape, measure), pad);
    return FRAME_HANDLE_IDS.map((id) => ({ id, at: handlePoint(frame, FRAME_HANDLES[id]) }));
}

export function handleAt(
    handles: readonly Handle[],
    point: Point,
    tolerance: number
): Handle | null {
    const nearest = handles.reduce<Handle | null>(
        (best, handle) =>
            !best || distance(handle.at, point) < distance(best.at, point) ? handle : best,
        null
    );
    return nearest && distance(nearest.at, point) <= tolerance ? nearest : null;
}

// Signed: dragging past the opposite side leaves left > right, which mirrors the mark.
interface Edges {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

function draggedEdges(frame: Box, id: FrameHandleId, pointer: Point, keepAspect: boolean): Edges {
    const axes = FRAME_HANDLES[id];
    const right = frame.x + frame.width;
    const bottom = frame.y + frame.height;
    const anchor = { x: axes.x === -1 ? right : frame.x, y: axes.y === -1 ? bottom : frame.y };
    const isCorner = axes.x !== 0 && axes.y !== 0;
    const target =
        isCorner && keepAspect
            ? keepAspectCorner(anchor, pointer, frame.width / frame.height)
            : pointer;
    return {
        left: axes.x === -1 ? target.x : frame.x,
        top: axes.y === -1 ? target.y : frame.y,
        right: axes.x === 1 ? target.x : right,
        bottom: axes.y === 1 ? target.y : bottom
    };
}

function scaleAxis(value: number, start: number, length: number, from: number, to: number): number {
    if (length === 0) return from + (value - start);
    return from + ((value - start) / length) * (to - from);
}

function mapIntoEdges(point: Point, frame: Box, edges: Edges): Point {
    return {
        x: scaleAxis(point.x, frame.x, frame.width, edges.left, edges.right),
        y: scaleAxis(point.y, frame.y, frame.height, edges.top, edges.bottom)
    };
}

// `frame` is the original's frameBox, captured when the drag started.
export function resizeShape(
    shape: MarkupShape,
    frame: Box,
    handle: HandleId,
    pointer: Point,
    constrain: boolean
): MarkupShape {
    if (isSegmentShape(shape)) {
        if (handle === 'from')
            return { ...shape, from: constrain ? snapAngle(shape.to, pointer) : pointer };
        if (handle === 'to')
            return { ...shape, to: constrain ? snapAngle(shape.from, pointer) : pointer };
        return shape;
    }
    if (shape.kind === 'text' || !isFrameHandle(handle)) return shape;
    const edges = draggedEdges(frame, handle, pointer, constrain);
    if (shape.kind === 'pen') {
        return { ...shape, points: shape.points.map((point) => mapIntoEdges(point, frame, edges)) };
    }
    const box = boxFromCorners(
        { x: edges.left, y: edges.top },
        { x: edges.right, y: edges.bottom }
    );
    return { ...shape, box };
}
